// src/pages/HeathrowCoachHire.jsx
import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import CcgPremiumReviews from "../components/CcgPremiumReviews";
import CcgRouteLhrStatusCompact from "../components/CcgRouteLhrStatusCompact";
import FloatingSupportChat from "../components/FloatingSupportChat";
import PostcodeQuotenewCoach from "./PostcodeQuotenewCoach";
import LiveStrip from "../components/LiveStrip";
import "./Home.css";
import "./CcgRoutePremium.css";
import "../styles/heathrowMinibus.css";

const seoTitle = "Heathrow Coach Hire – 25+ Seater Airport & Event Coaches | CheapCabGrab";
const seoDescription = "Book 25+ seater Heathrow coaches for airport transfers, corporate events, tours and school trips. Fixed-price quotes, professional PSV partner coaches and group travel support.";
const seoCanonical = "https://www.cheapcabgrab.com/heathrow-coach-hire";
const seoKeywords = "Heathrow coach hire, 25 seater coach Heathrow, airport coach transfer, group coach hire Heathrow, CheapCabGrab coach";
const seoOgTitle = "Heathrow Coach Hire – 25+ Seater Airport & Event Coaches";
const seoOgDescription = "Large group airport transfers & event transport from Heathrow with 25+ seater coaches, fixed fares and professional PSV partners.";

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "BusTrip",
  name: "CheapCabGrab – Heathrow Coach Hire",
  provider: {
    "@type": "Organization",
    name: "CheapCabGrab",
  },
  areaServed: "Heathrow Airport and Greater London",
  url: seoCanonical,
};

const HeathrowCoachHire = () => {
  const scrollToForm = () => {
    const el = document.getElementById("heathrow-coach-quote-form");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    const setMeta = (selector, attrs) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([key, value]) => el.setAttribute(key, value));
    };

    const applySeo = () => {
      document.title = seoTitle;

      let canonicalEl = document.querySelector('link[rel="canonical"]');
      if (!canonicalEl) {
        canonicalEl = document.createElement("link");
        canonicalEl.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalEl);
      }
      canonicalEl.setAttribute("href", seoCanonical);

      setMeta('meta[name="description"]', { name: "description", content: seoDescription });
      setMeta('meta[name="keywords"]', { name: "keywords", content: seoKeywords });
      setMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
      setMeta('meta[property="og:title"]', { property: "og:title", content: seoOgTitle });
      setMeta('meta[property="og:description"]', { property: "og:description", content: seoOgDescription });
      setMeta('meta[property="og:url"]', { property: "og:url", content: seoCanonical });

      let script = document.querySelector('script[data-ccg-jsonld="heathrow-coach-hire"]');
      if (!script) {
        script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-ccg-jsonld", "heathrow-coach-hire");
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    };

    applySeo();
    const timers = [window.setTimeout(applySeo, 0), window.setTimeout(applySeo, 750)];
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, []);

  return (
    <div className="heathrow-coach-page route-page ccg-route-premium">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={seoCanonical} />
        <meta name="keywords" content={seoKeywords} />

        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoOgTitle} />
        <meta property="og:description" content={seoOgDescription} />
        <meta property="og:url" content={seoCanonical} />

        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      </Helmet>

      <LiveStrip variant="heathrow" />

      <main className="heathrow-coach-main route-main">
        <section className="ccg-route-hero ccg-heathrow-coach-hero">
          <div className="ccg-route-hero-shell">
            <div className="ccg-route-hero-top">
              <div className="ccg-route-hero-copy">
                <span className="ccg-route-eyebrow">Heathrow group coach transport</span>
                <h1>Heathrow Coach Hire – 25+ Seater Group Transport</h1>
                <p>Airport &amp; event specialists for large groups</p>
                <p>
                  Organise reliable coach transport from Heathrow to London, hotels and venues.
                  Fixed-price quotes, licensed PSV partner coaches and space for teams,
                  conferences and school groups.
                </p>
              </div>

              <div className="ccg-route-trust-cards" aria-label="CheapCabGrab Heathrow coach trust signals">
                <article className="ccg-route-trust-card">
                  <b>✓</b>
                  <span>
                    <small>PSV partners</small>
                    <strong>25-53 seats</strong>
                    <span>Large group airport transport</span>
                  </span>
                </article>
                <article className="ccg-route-trust-card">
                  <b>G</b>
                  <span>
                    <small>Google reviews</small>
                    <strong>4.8 ★★★★★</strong>
                    <span>Trusted group transfers</span>
                  </span>
                </article>
              </div>
            </div>

            <div className="ccg-route-quote-wrap" id="heathrow-coach-quote-form">
              <div className="ccg-route-quote-head">
                <span className="ccg-route-quote-label">Get instant Heathrow coach quotes</span>
                <span className="ccg-route-quote-note"><i /> Free - Fast - No obligation</span>
              </div>
              <PostcodeQuotenewCoach presetPickup="Heathrow Airport" />
              <ul className="ccg-route-quote-promises">
                <li>Fixed upfront fare</li>
                <li>Licensed PSV partners</li>
                <li>Flight-aware planning</li>
                <li>Group travel support</li>
              </ul>
            </div>

            <ul className="ccg-route-flags">
              <li>25-53 seater coaches for groups and events</li>
              <li>Heathrow, London hotels, stadiums and cruise transfers</li>
              <li>Fixed upfront quotes — no surge pricing</li>
              <li>Licensed PSV partners, professional drivers and luggage-ready vehicles</li>
            </ul>

            <div className="ccg-route-review-strip">
              <CcgPremiumReviews pageKey="/heathrow-coach-hire" ariaLabel="Heathrow coach customer reviews" />
            </div>
          </div>
        </section>

        <CcgRouteLhrStatusCompact title="Live Heathrow checks for coach bookings" />

        <button className="route-floating-book-btn" onClick={scrollToForm}>
          Book Heathrow Coach
        </button>

        {/* FLEET */}
        <section className="heathrow-coach-section" id="fleet">
          <header className="heathrow-coach-section-header">
            <h2>🚌 Our Heathrow Coach Options</h2>
            <p>
              Comfortable, air-conditioned coaches operated by carefully vetted
              PSV partners.
            </p>
          </header>

          <div className="heathrow-coach-fleet-grid">
            <article className="heathrow-coach-fleet-card">
              <img
                src="/coach-hero.png"
                alt="25 seater Heathrow coach"
                className="heathrow-coach-fleet-img"
              />
              <h3>25–29 Seater Midi Coach</h3>
              <ul>
                <li>👥 Ideal for 20–29 passengers</li>
                <li>🧳 Good luggage capacity for airport transfers</li>
                <li>✅ Popular for corporate groups & European tours</li>
                <li>✅ Great for school trips and sports teams</li>
              </ul>
            </article>

            <article className="heathrow-coach-fleet-card subtle-card">
              <img
                src="/coach.jpeg"
                alt="Large Heathrow coach"
                className="heathrow-coach-fleet-img"
              />
              <h3>Larger Coaches (30–50+ Seats)</h3>
              <p>
                For larger groups, multiple coaches or shuttle operations,
                contact us with your full itinerary and passenger numbers.
              </p>
              <p>
                We coordinate with licensed PSV partners such as{" "}
                <strong>RHP Minibus Hire UK Limited</strong> to provide
                high-capacity vehicles.
              </p>
              <Link to="/contact" className="heathrow-coach-link">
                Talk to our group travel planners →
              </Link>
            </article>
          </div>
        </section>

        {/* FARES */}
        <section className="heathrow-coach-section" id="fares">
          <header className="heathrow-coach-section-header">
            <h2>💷 Example Heathrow Coach Routes &amp; Pricing</h2>
            <p>
              These are guide prices for single journeys. We’ll confirm exact
              pricing on your quote depending on time, route and duration.
            </p>
          </header>

          <div className="heathrow-coach-fares-grid">
            <article className="heathrow-coach-fare-card">
              <h3>Heathrow ↔ Central London Hotels</h3>
              <ul>
                <li>📍 16–22 miles</li>
                <li>⏱ 45–90 minutes (traffic-dependent)</li>
                <li>
                  💷 25+ seater coach typically from{" "}
                  <strong>£260–£380</strong>
                </li>
              </ul>
            </article>

            <article className="heathrow-coach-fare-card">
              <h3>Heathrow ↔ Conference Venues &amp; Stadiums</h3>
              <ul>
                <li>📍 ExCeL, O2, Wembley, Twickenham &amp; more</li>
                <li>⏱ 45–90 minutes</li>
                <li>
                  💷 Event-day pricing from{" "}
                  <strong>£300–£450</strong> depending on waiting &amp; parking
                </li>
              </ul>
            </article>

            <article className="heathrow-coach-fare-card">
              <h3>Heathrow ↔ Schools &amp; Universities</h3>
              <ul>
                <li>📍 Across London &amp; the South East</li>
                <li>⏱ 45–120+ minutes depending on location</li>
                <li>
                  💷 Competitive education-rate packages –{" "}
                  <Link to="/contact">ask our team</Link>
                </li>
              </ul>
            </article>

            <article className="heathrow-coach-fare-card">
              <h3>Shuttles &amp; Multi-Coach Operations</h3>
              <p>
                For shuttles between hotels and venues, or multiple coaches for
                large conferences, we’ll build a tailored proposal.
              </p>
              <Link to="/contact" className="heathrow-coach-link">
                Request a tailored coach plan →
              </Link>
            </article>
          </div>
        </section>

        {/* FAQ */}
        <section className="heathrow-coach-section" id="faq">
          <header className="heathrow-coach-section-header">
            <h2>❓ Heathrow Coach Hire FAQs</h2>
            <p>Key details groups ask before booking coaches via CheapCabGrab.</p>
          </header>

          <div className="heathrow-coach-faq-grid">
            <article className="heathrow-coach-faq-card">
              <h3>Who operates the coaches?</h3>
              <p>
                Coaches are operated by fully licensed PSV partners such as{" "}
                <strong>RHP Minibus Hire UK Limited</strong> and other vetted
                operators. CheapCabGrab coordinates booking, pricing and
                planning.
              </p>
            </article>

            <article className="heathrow-coach-faq-card">
              <h3>Can you handle complex schedules?</h3>
              <p>
                Yes – our team can plan hotel shuttles, venue transfers and
                staggered pickups. Share your schedule and we’ll suggest the
                most efficient plan.
              </p>
            </article>

            <article className="heathrow-coach-faq-card">
              <h3>Do prices include parking and waiting?</h3>
              <p>
                We’ll clearly show what’s included. For events, parking and
                extended waiting are usually priced separately and agreed in
                advance.
              </p>
            </article>

            <article className="heathrow-coach-faq-card">
              <h3>Can we brand or sign the coaches?</h3>
              <p>
                For larger corporate events, temporary signage may be possible.
                Let us know your requirements and we’ll confirm with our
                partners.
              </p>
            </article>
          </div>
        </section>

        {/* SUPPORT BANNER */}
        <section className="heathrow-coach-support">
          <div className="heathrow-coach-support-inner">
            <div>
              <h2>📞 Planning a large event or delegation?</h2>
              <p>
                Share your dates, group sizes and venues and we’ll build a coach
                transport plan that works for you.
              </p>
            </div>
            <div className="heathrow-coach-support-actions">
              <Link to="/contact" className="heathrow-coach-support-btn">
                Contact coach planning team →
              </Link>
              <Link to="/" className="heathrow-coach-support-link">
                Or start with an instant online quote →
              </Link>
            </div>
          </div>
        </section>

        <div className="route-bottom-cta">
          <button className="route-bottom-cta-btn" onClick={scrollToForm}>
            Book Heathrow Coach
          </button>
        </div>

        <div className="heathrow-coach-bottom-spacer" />
      </main>

      <FloatingSupportChat
        brand="ccg"
        defaultFlow="enquiry"
        position="right"
        showAfterMs={9000}
        nudgeText="Need Heathrow coach help?"
      />
    </div>
  );
};

export default HeathrowCoachHire;
